import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { useAuth0 } from '@auth0/auth0-vue'
import { permissionGuard } from '@/lib/permissionGuard'
import { roleService } from '@/lib/roleService'
import { supabase } from '@/lib/supabaseClient'
import { createLogger } from '@/lib/logger'

const log = createLogger('Auth0Store')

/** Full admin permissions — unrestricted access to all features */
const ADMIN_PERMISSIONS = [
  'manage_users',
  'manage_roles',
  'view_forms',
  'edit_forms',
  'manage_forms',
  'view_reports',
  'export_reports',
  'delete_forms',
  'manage_dropdowns',
  'manage_equipment',
  'view_equipment',
  'view_consignees',
  'checkout_equipment',
  'return_equipment',
  'track_equipment',
  'view_users',
  'invite_users',
  'view_audit_logs',
  'system_admin',
  'view_roles',
  'view_dashboard',
  'view_inventory',
  'view_picker',
  'scan_equipment',
  'view_my_equipment',
  'view_calendar',
  'view_tracking',
  'view_docs',
]

/**
 * Sales engineer: manage own equipment, checkout/return, picker, calendar, tracking.
 * Everything a field user needs — but NOT admin tools or user management.
 */
const SALES_ENGINEER_PERMISSIONS = [
  'view_dashboard',
  'view_inventory',
  'view_picker',
  'scan_equipment',
  'view_my_equipment',
  'view_calendar',
  'view_tracking',
  'view_docs',
  'view_equipment',
  'view_consignees',
  'checkout_equipment',
  'return_equipment',
  'track_equipment',
  'view_forms',
  'edit_forms',
  'view_reports',
]

/**
 * Read-only support / viewer: can see everything the admin sees EXCEPT
 * Admin Tools and Admin sections (no user management, no role management,
 * no equipment catalog editing, no system admin).
 */
const READONLY_SUPPORT_PERMISSIONS = [
  'view_dashboard',
  'view_inventory',
  'view_picker',
  'view_my_equipment',
  'view_calendar',
  'view_tracking',
  'view_docs',
  'view_equipment',
  'view_consignees',
  'view_forms',
  'view_reports',
  'view_users',
  'view_roles',
  'view_audit_logs',
]

const FALLBACK_ADMIN_EMAILS = new Set<string>(['greg@gregc.io', 'jmchale@lingo.com'])

export const useAuth0Store = defineStore('auth0', () => {
  const auth0 = useAuth0()

  const isAuthenticated = computed(() => auth0.isAuthenticated.value)
  const isLoading = computed(() => auth0.isLoading.value)
  const user = computed(() => auth0.user.value)
  const error = computed(() => auth0.error.value)

  // Role-based permissions
  const userRoles = ref<string[]>([])
  const userPermissions = ref<string[]>([])
  const roleLoading = ref(false)
  /** Supabase user UUID resolved from Auth0 email via admin_users bridge. */
  const supabaseUserId = ref<string | null>(null)

  // Admin check - based strictly on roles loaded from backend
  const isAdmin = computed(() => {
    if (!user.value) return false
    return userRoles.value.includes('admin')
  })

  // Permission checks
  const hasPermission = computed(() => (permission: string) => {
    return userPermissions.value.includes(permission)
  })

  const hasRole = computed(() => (role: string) => {
    return userRoles.value.includes(role.toLowerCase())
  })

  const canManageUsers = computed(() => hasPermission.value('manage_users'))
  const canManageRoles = computed(() => hasPermission.value('manage_roles'))
  const canViewForms = computed(() => hasPermission.value('view_forms'))
  const canEditForms = computed(() => hasPermission.value('edit_forms'))
  const canManageForms = computed(() => hasPermission.value('manage_forms'))
  const canViewReports = computed(() => hasPermission.value('view_reports'))

  const displayName = computed(() => {
    if (!user.value) return 'Guest'
    return user.value.name || user.value.email?.split('@')[0] || 'User'
  })

  const initials = computed(() => {
    if (!user.value) return '??'
    const name = user.value.name || user.value.email?.split('@')[0] || 'User'
    return (
      name
        .split(/\s+/)
        .map((part) => part.trim()[0])
        .filter(Boolean)
        .join('')
        .slice(0, 2)
        .toUpperCase() || 'US'
    )
  })

  // Load user roles and permissions
  async function loadUserRoles() {
    log.debug('loadUserRoles called, user.value:', user.value)
    if (!user.value?.sub) {
      log.debug('No user sub, returning early')
      return
    }

    roleLoading.value = true
    try {
      // Since we're using Auth0, we need to find the Supabase user by email
      // The Auth0 user ID is different from the Supabase user ID
      const auth0Email = user.value.email
      log.debug('Looking up Supabase user by email:', auth0Email)

      // Resolve Supabase user UUID from Auth0 email for cross-system lookups
      const { data: resolvedId } = await supabase.rpc('get_user_id_by_email', {
        email_param: (auth0Email || '').toLowerCase(),
      })
      supabaseUserId.value = resolvedId ?? null
      log.debug('Resolved Supabase user ID:', supabaseUserId.value)

      // Use Supabase Admin API to get user by email
      const roles = await roleService.getUserRolesForEmail(auth0Email || '')
      log.debug('Raw roles from roleService:', roles)
      userRoles.value = roles
        .map((role) => (typeof role.name === 'string' ? role.name.toLowerCase() : ''))
        .filter((name): name is string => Boolean(name))
      log.debug('Normalized role names:', userRoles.value)

      let isFallbackAdmin = false
      if (
        userRoles.value.length === 0 &&
        auth0Email &&
        FALLBACK_ADMIN_EMAILS.has(auth0Email.toLowerCase())
      ) {
        log.warn('Applying fallback admin role for email:', auth0Email)
        userRoles.value = ['admin']
        isFallbackAdmin = true
      }

      // Load permissions for each role
      const allPermissions: string[] = []
      if (isFallbackAdmin) {
        allPermissions.push(...ADMIN_PERMISSIONS)
      }
      for (const role of roles) {
        const normalizedRole = typeof role.name === 'string' ? role.name.toLowerCase() : ''
        try {
          switch (normalizedRole) {
            case 'admin':
              allPermissions.push(...ADMIN_PERMISSIONS)
              break
            case 'sales_engineer':
              allPermissions.push(...SALES_ENGINEER_PERMISSIONS)
              break
            case 'lingo_partner_support':
            case 'lingo_sales_support':
            case 'viewer':
              allPermissions.push(...READONLY_SUPPORT_PERMISSIONS)
              break
            case 'partner':
              allPermissions.push(
                ...READONLY_SUPPORT_PERMISSIONS,
                'view_partner_profile',
                'edit_partner_profile',
                'view_partner_notifications',
              )
              break
            default:
              log.warn(`Unknown role "${normalizedRole}" — granting basic view permissions`)
              allPermissions.push('view_dashboard', 'view_equipment', 'view_forms')
              break
          }
        } catch (error) {
          log.warn(`Failed to load permissions for role ${role.name}:`, error)
        }
      }

      userPermissions.value = [...new Set(allPermissions)] // Remove duplicates

      log.debug('Loaded user roles:', roles)
      log.debug('Loaded user permissions:', userPermissions.value)
    } catch (error) {
      log.error('Failed to load user roles:', error)
      const auth0Email = user.value?.email?.toLowerCase() || ''
      if (!userRoles.value.length && FALLBACK_ADMIN_EMAILS.has(auth0Email)) {
        log.warn('Applying fallback admin role after role load failure for:', auth0Email)
        userRoles.value = ['admin']
        userPermissions.value = [...new Set(ADMIN_PERMISSIONS)]
      }
    } finally {
      roleLoading.value = false
    }
  }

  // Watch for authentication changes and set token for Supabase
  watch(
    isAuthenticated,
    async (authenticated) => {
      if (authenticated) {
        try {
          // Get access token (API audience) and ID token (OIDC claims)
          const token = await auth0.getAccessTokenSilently({
            // Request the same scopes explicitly to keep renewal behavior consistent.
            authorizationParams: {
              scope: 'openid profile email offline_access read:data write:data',
            },
          })

          if (token) {
            ;(window as any).__auth0Token = token
            try {
              const idClaims = auth0.idTokenClaims.value
              ;(window as any).__auth0IdToken = idClaims?.__raw || null
            } catch {
              ;(window as any).__auth0IdToken = null
            }
            log.debug('ID Token set for Supabase API calls')

            // Debug: Check token format
            try {
              const payload = JSON.parse(atob(token.split('.')[1]))
              log.debug('ID Token payload:', payload)
              log.debug('Has role claim:', payload.role)
            } catch (e) {
              log.debug('Could not decode ID token for debugging')
            }
          } else {
            log.error('No ID token available')
          }

          // Load user roles and permissions
          await loadUserRoles()
        } catch (error) {
          log.error('Failed to get token:', error)
          ;(window as any).__auth0Token = null
          ;(window as any).__auth0IdToken = null
          
        }
      } else {
        ;(window as any).__auth0Token = null
        ;(window as any).__auth0IdToken = null
        userRoles.value = []
        userPermissions.value = []
        log.debug('Token cleared')
        
      }
    },
    { immediate: true },
  )

  // Auth methods
  const login = (returnTo?: string) => {
    log.debug('Initiating login...')
    return auth0.loginWithRedirect({
      appState: returnTo ? { target: returnTo } : undefined,
      authorizationParams: {
        redirect_uri: window.location.origin,
      },
    })
  }

  const logout = async () => {
    log.debug('Logging out...')
    ;(window as any).__auth0Token = null
    return auth0.logout({
      logoutParams: {
        returnTo: window.location.origin + '/signin',
      },
    })
  }

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    isAdmin,
    displayName,
    initials,
    login,
    logout,
    // Role-based permissions
    supabaseUserId,
    userRoles,
    userPermissions,
    roleLoading,
    hasPermission,
    hasRole,
    canManageUsers,
    canManageRoles,
    canViewForms,
    canEditForms,
    canManageForms,
    canViewReports,
    loadUserRoles,
  }
})
